/**
 * "What's new" — the release showcase shown after sign-in and reachable from
 * the user menu. Content-as-code: each slide names the audience that should
 * see it (the same capability booleans the app gates its UI with) and the
 * Knowledge article it leads to. Bump WHATS_NEW_KEY whenever the slides change
 * so a "Don't show again" from an earlier release doesn't hide the new ones.
 *
 * Art lives in components/WhatsNewModal.jsx (JSX); this module stays plain JS
 * so the gating can be unit-tested.
 */
export const WHATS_NEW_KEY = '2026-09-saved-views';

const clinicUser = (u) => u?.role === 'ClinicManager' || u?.role === 'ClinicMailbox';
const supervisorUser = (u) =>
  !!u?.canManageSla || !!u?.canManageClinicAccess || !!u?.adminTabs
  || u?.role === 'HoRO' || u?.role === 'Supervisor' || !!u?.isSuperAdmin;

export const WHATS_NEW_SLIDES = [
  {
    key: 'sla-clinic', tag: 'New',
    title: 'A response clock — and Acknowledge',
    desc: 'When a Supervisor writes on one of your cases, a response clock starts for the clinic. Press Acknowledge on their message to show it has been seen and pause the clock. The chip on the case always says whose turn it is.',
    article: 'response-sla',
    visibleTo: (u) => clinicUser(u) && !u?.canManageSla,
  },
  {
    key: 'sla-supervisor', tag: 'New',
    title: 'Response SLA: holds, snooze and Manage',
    desc: 'Every coded treatment case carries a response clock that alternates between you and the clinic. An acknowledgement holds it as Waiting on Supervisor; snooze reminders from + Add, or use Manage to resume, suppress, or ask for further action.',
    article: 'response-sla',
    visibleTo: (u) => !!u?.canManageSla,
  },
  {
    key: 'waiver', tag: 'New',
    title: 'Record that there is no consent form',
    desc: 'If a consent form was never taken, say so instead of leaving the slot open. Record no consent form marks the slot amber with your reason, and it can be withdrawn later if the form turns up.',
    article: 'attachments',
    visibleTo: (u) => !!u?.canWaiveRequirements,
  },
  {
    key: 'stale-cases', tag: 'Improved',
    title: 'Find stale cases faster',
    desc: 'All Cases has Last activity and SLA columns, an Inactive-for filter, a column chooser and Selected / All / Deleted views. Sort by SLA to bring the most urgent cases to the top.',
    article: 'case-list',
    visibleTo: supervisorUser,
  },
  {
    key: 'saved-views', tag: 'New',
    title: 'Save your own views',
    desc: 'Set the filters, columns and sort you like on All Cases, then tap the star by the title to save it as a named view. Pick any of your views from the chevron next to “All Cases”, and mark one as your default. Views are private to you.',
    article: 'case-list',
    visibleTo: (u) => !clinicUser(u),
  },
  {
    key: 'columns', tag: 'New',
    title: 'Choose the columns you work with',
    desc: 'The table button beside Deleted picks which columns are in view. Turn on SLA to see the response clock in the list, hide what you never read, and the choice is remembered on this device.',
    article: 'case-list',
    visibleTo: () => true,
  },
  {
    key: 'pickers', tag: 'Improved',
    title: 'Filter by what a case has — or hasn’t',
    desc: 'Countries, clinics and tags are picked from a dialog and shown as pills you can remove one by one. Each tag has three states: has, off, or doesn’t have, so you can exclude as well as include.',
    article: 'tags',
    visibleTo: supervisorUser,
  },
  {
    key: 'approvals', tag: 'New',
    title: 'Clinic staff approvals inbox',
    desc: 'Requests from clinic staff for access and PIN resets land in Notifications. Approve, or decline with a reason; the person sees the outcome next time they sign in.',
    article: 'clinic-staff-admin',
    visibleTo: (u) => !!u?.canManageClinicAccess,
  },
  {
    key: 'clinic-signin', tag: 'New',
    title: 'Sign in on the shared clinic device',
    desc: 'On the clinic mailbox login, pick your name and enter your PIN to work as yourself. Your comments and uploads carry your name, and Lock hands the device back when you step away.',
    article: 'clinic-signin',
    visibleTo: (u) => u?.role === 'ClinicMailbox',
  },
  {
    key: 'privacy', tag: 'New',
    title: 'Privacy mode for photo previews',
    desc: 'Switch on Privacy in the user menu to frost photo thumbnails on case files, for when others can see your screen. Open a photo to view it in full; thumbnails stay frosted until you switch it off.',
    article: 'privacy-mode',
    visibleTo: () => true,
  },
  {
    key: 'case-layout', tag: 'Improved',
    title: 'A tidier case page',
    desc: 'Severity and status are controls in the header, the response clock and the legal deadline share one row, and incident details sit in two columns so nothing needs scrolling to find.',
    article: 'case-detail-tour',
    visibleTo: () => true,
  },
];

/** The slides this user should see, in showcase order. */
export function slidesFor(user) {
  return WHATS_NEW_SLIDES.filter(s => {
    try { return !!s.visibleTo(user); } catch { return false; }
  });
}
